import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { UserApiService } from '../../../core/services/user-api.service';
import { RequestFormStateService } from '../../../core/services/request-form-state.service';
import { AdminManageService } from '../../../core/services/adminService/admin-manage.service';
import {
  Attachment,
  PnaDetailsDto,
  Request,
  Comment,
} from '../../../core/interfaces/users';
import { apiEndpoints } from '../../../core/constants/api-endpoints';
import { constantTxt } from '../../../core/constants/constants';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzTableModule } from 'ng-zorro-antd/table';
import { CompanyCodeService } from '../../../core/services/adminService/company-code.service';
import { firstValueFrom, skip, Subscription } from 'rxjs';
import { LeadBuyerServiceService } from '../../../core/services/Lead Buyers/lead-buyer-service.service';
import { BuyerServiceService } from '../../../core/services/buyers/buyer-service.service';
import { PopupComponent } from '../../shared/popup/popup.component';
import { ExportExcelService } from '../../../core/services/export-excel.service';
import { FileService } from '../../../core/services/file.service';

@Component({
  selector: 'app-add-request',
  templateUrl: './add-request.component.html',
  styleUrls: ['./add-request.component.less'],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    RouterModule,
    NzButtonModule,
    NzSelectModule,
    NzInputModule,
    NzIconModule,
    NzCheckboxModule,
    NzTableModule,
    PopupComponent,
  ],
})
export class AddRequestComponent implements OnInit, OnDestroy {
  mode: string | null = null;
  role: string = '';

  isUser: boolean = false;
  isLeadBuyer: boolean = false;
  isBuyer: boolean = false;
  isAdmin: boolean = false;

  isEditMode: boolean = false;
  isAddMode: boolean = false;
  isViewMode: boolean = false; // View & Overview Mode

  pnano: string = '';
  pnayear: string = '';
  status: string = '';
  welcome: string = '';
  pnaIdTemp: string = '';
  returnPath: string = '';

  items: any[] = [];
  selectedItems: any[] = [];
  pnaNoTemp: number = 0;
  pnaYearTemp: number = 0;

  showResults: boolean = false;
  isBranchPlantEnabled: boolean = false;
  branchPlant: string = '';

  locations: any[] = [];
  purchasingOffices: any[] = [];
  companyCodes: any[] = [];
  pnaLocation: any[] = [];
  activeBuyers: any[] = [];
  selectedLocation: string = '';
  selectedPurchasingOffice: string = '';
  selectedCompanyCode: string = '';
  groupEmail: string = '';
  requestorRatt: string = '';
  requestorName: string = '';
  alternateEmail: string = '';
  pnaTitle: string = '';
  selectedEmail: string = '';
  selectedAssignBuyer: string = '';
  data: any[] = [];
  attachments: Attachment[] = [];
  comments: Comment[] = [];
  requestData!: Request;
  itemForm!: FormGroup;
  manageForm!: FormGroup;
  getPnaNo: number | null = null;
  getPnaYear: string | null = null;
  rawEmail: string = '';
  constants = constantTxt;
  disableReassign: boolean = false;
  disableAssign: boolean = false;
  disableBack: boolean = false;
  disableReset: boolean = false;
  returnUrlRole: string = '';
  disableSubmit: boolean = false;
  showPopup: boolean = false;
  popupData!: any;
  commentAttachmentfile!: any;
  customNoDataMessage = constantTxt.noDataFound;

  getLocationsSubscription!: Subscription;
  companiesSubscription!: Subscription;
  officeSubscription!: Subscription;
  saveOrUpdatePnASubscription!: Subscription;
  pnaRequestLeadBuyerActionSubscription!: Subscription;
  pnaRequestBuyerActionSubscription!: Subscription;
  updatePnaRequestSubscription!: Subscription;
  downloadFileSubscription!: Subscription;
  removeBuyersAttachmentSubscription!: Subscription;

  constructor(
    private readonly router: Router,
    private readonly userApi: UserApiService,
    private readonly fb: FormBuilder,
    private readonly zone: NgZone,
    private readonly formState: RequestFormStateService,
    private readonly adminService: AdminManageService,
    private readonly companyCodeService: CompanyCodeService,
    private readonly leadBuyerServiceService: LeadBuyerServiceService,
    private readonly buyerServiceService: BuyerServiceService,
    private readonly adminManageService: AdminManageService,
    private readonly excelExportService: ExportExcelService,
    private readonly fileService: FileService
  ) {}

  ngOnInit() {
    this.initializeForm();
    this.setupModeAndViewState();
    this.initializeFormData();
    this.loadApiDate();

    this.setupFormWatchers();
  }

  private initializeForm(): void {
    this.manageForm = this.fb.group({
      pnaLocation: [''],
      requestorName: [''],
      requestorEmail: [''],
      pnaTitle: [''],
      selectedCompanyCode: [''],
      branchPlant: [''],
      groupEmail: [''],
      selectedPurchasingOffice: [''],
      requestorRatt: [''],
      alternateEmail: [''],
      comments1: [''],
      assignBuyer: [''],
    });
  }

  private initializeFormData(): void {
    const saved = this.formState.getFormData();
    if (saved) {
      this.manageForm.patchValue(saved);
    }
  }

  private setupModeAndViewState(): void {
    // Use navigation state as the primary source
    const navigationState = history.state;

    if (navigationState) {
      this.role = navigationState.role;
      this.pnaIdTemp = navigationState.pnaIdTemp;
      this.returnPath = navigationState.returnUrl;

      // Setting Mode's
      this.mode = navigationState.mode || null;
      this.isAddMode = this.mode === 'add';
      this.isEditMode = this.mode === 'edit';
      this.isViewMode = this.mode === 'view' || this.mode === 'otherview';

      this.isUser = this.role === 'user';
      this.isBuyer = this.role === 'buyer';
      this.isLeadBuyer = this.role === 'lead-buyer';
      this.isAdmin = this.role === 'admin';

      this.getPnaNo = navigationState.pnaNo
        ? Number(navigationState.pnaNo)
        : null;
      this.getPnaYear = navigationState.year ?? null;

      this.returnUrlRole = this.isUser
        ? 'user'
        : this.isLeadBuyer
        ? 'lead-buyer'
        : this.isBuyer
        ? 'buyer'
        : this.isAdmin
        ? 'admin'
        : '';
    }
  }

  async loadApiDate() {
    this.loadDropdownData();
    this.setUserInformation();
    await this.loadPNAData();
    this.settingControlsPerMode();
  }

  private loadDropdownData(): void {
    this.companiesSubscription = this.companyCodeService
      .getCompanies()
      .subscribe({
        next: (res) => {
          let data = res.body;
          if (data) {
            this.companyCodes = data;
          }
        },
        error: (error) => {
          console.error('Error loading company codes:', error);
        },
      });
    this.getLocationsSubscription = this.userApi.getLocations().subscribe({
      next: (res) => {
        let data = res.body;
        if (data) {
          this.pnaLocation = data;
        }
      },
      error: (error) => {
        console.error('Error loading locations:', error);
      },
    });
    this.officeSubscription = this.userApi.getOfficesList().subscribe({
      next: (res) => {
        let data = res.body;
        if (data) {
          this.purchasingOffices = data.filter((x) => x.purchasingStatus);
        }
      },
      error: (error) => {
        console.error('Error loading purchasing offices:', error); // Add this log
      },
    });
  }

  private setUserInformation(): void {
    this.rawEmail = sessionStorage.getItem('UserEmail') || '';

    const rawUserName =
      sessionStorage.getItem('UserName') ||
      sessionStorage.getItem('UserEmail') ||
      '';
    let formattedUserName = '';

    if (rawUserName) {
      const nameMatch = rawUserName.match(/^([^,]+),\s*([^[]+)/);
      if (nameMatch) {
        const lastName = nameMatch[1].trim();
        const firstName = nameMatch[2].trim();
        formattedUserName = `${firstName} ${lastName}`;
      } else {
        formattedUserName = rawUserName;
      }
    }

    if (!this.getPnaNo && !this.getPnaYear) {
      this.manageForm.get('requestorName')?.setValue(formattedUserName);
    }
  }

  private async loadPNAData(): Promise<void> {
    this.clearLocalStorage();

    if (this.getPnaNo && this.getPnaYear) {
      await this.fetchRequestDetails(this.getPnaNo.toString(), this.getPnaYear);
    } else {
      await this.loadItems();
    }
  }

  // Fetch request details based on PNA number and year
  async fetchRequestDetails(pnaNo: string, year: string): Promise<void> {
    const response = await firstValueFrom(
      this.userApi.getRequestDetails(pnaNo, year)
    );
    if (response) {
      const data = response.body;
      if (data) {
        this.comments = data.comments || [];
        this.items = data.items || [];
        this.pnano = data.request.pnaNo ? data.request.pnaNo.toString() : '';
        this.pnayear = year;
        this.status = data.request.pnaStatus ?? '';
        this.welcome =
          data.request.pnaRequestorFullName ?? data.request.pnaRequestor ?? '';

        let requestData = data.request;
        this.manageForm.patchValue(
          {
            pnaLocation: requestData.pnaLocation,
            groupEmail: requestData.pnaGroupMail,
            requestorName: requestData.pnaRequestor,
            requestorEmail: requestData.pnaRequestorCai,
            alternateEmail: requestData.pnaCoOwner,
            pnaTitle: requestData.pnaTitle,
            requestorRatt: requestData.pnaRequestorRef,
            selectedPurchasingOffice: requestData.pnaPurchasing,
            branchPlant: requestData.pnaBranch,
            //comments1: requestData.pnaComment, // Comment field is not used in the form
            selectedCompanyCode: requestData.pnaCcn,
          },
          { emitEvent: false }
        );

        // Fetching active buyers if purchasing office is set for admin or lead buyer
        if (
          data.request.pnaPurchasing &&
          (this.isAdmin || this.isBuyer || this.isLeadBuyer)
        ) {
          await this.fetchActiveBuyers(data.request.pnaPurchasing);

          const buyerId = this.activeBuyers.find(
            (b) => b.userId == data.request.pnaBuyer
          )?.userId;
          if (buyerId !== undefined) {
            this.manageForm
              .get('assignBuyer')
              ?.setValue(buyerId, { emitEvent: false });
          }
        }
      } else {
        console.warn('No data in response body');
      }
    }
  }

  // Fetch item list based on PNA ID Temp
  async loadItems(): Promise<void> {
    const response = await firstValueFrom(
      this.userApi.getItemList(Number(this.pnaIdTemp))
    );
    if (response) {
      let data = response.body;
      if (data && Array.isArray(data)) {
        this.items = data.map((item) => ({
          itemId: item.itemId,
          itemDesc: item.itemDesc,
          itemQuantity: item.itemQuantity,
          itemUom: item.itemUom,
          itemJdeItemNo: item.itemJdeItemNo,
          itemSuggestSupplier: item.itemSuggestSupplier,
          itemPriority: item.itemPriority,
          itemAttachment: item.itemAttachment || null,
          checked: false,
          itemNo: item.itemNoTemp,
        }));
        this.pnaYearTemp = data[0]?.itemPnaYearTemp;
      } else {
        this.items = [];
      }

      if (this.items.length === 0 && !this.pnaIdTemp) {
        localStorage.removeItem('currentPnaNo');
      }
    }
  }

  // Apply form restrictions based on user role and mode
  settingControlsPerMode() {
    if (this.isViewMode) {
      this.setViewMode();
    }

    if (this.isEditMode) {
      this.setEditMode();
    }
  }

  // Disables all controls because of VIEW mode
  private setViewMode() {
    Object.keys(this.manageForm.controls).forEach((key) => {
      this.manageForm.get(key)?.disable();
    });
  }

  // Disable all  controls except COMMENT
  private setEditMode() {
    if (this.isEditMode) {
      if (this.isUser && this.isOpen) {
        // For user role, disable all controls except comments
        Object.keys(this.manageForm.controls).forEach((key) => {
          if (key !== 'comments1') {
            this.manageForm.get(key)?.disable();
          } else {
            this.manageForm.get(key)?.enable();
          }
        });
      } else if (this.isLeadBuyer || this.isBuyer || this.isAdmin) {
        // For lead buyer, buyer, and admin roles, enable specific controls
        Object.keys(this.manageForm.controls).forEach((key) => {
          if (
            key !== 'selectedPurchasingOffice' &&
            key !== 'comments1' &&
            key !== 'assignBuyer'
          ) {
            this.manageForm.get(key)?.disable();
          } else {
            this.manageForm.get(key)?.enable();
          }
        });

        // For buyer role, disable assign buyer and reassign buttons
        if (this.isBuyer) {
          this.disableAssign = true;
          this.disableReassign = true;
        }
      }
    }
  }

  onPurchasingOfficeChange(): void {
    let manageForm = this.manageForm;
    manageForm.controls['assignBuyer'].disable({ emitEvent: false });
    this.disableAssign = true;
    this.disableReassign = false;
    this.disableSubmit = true;
  }

  onAssignBuyerChange(): void {
    let manageForm = this.manageForm;
    if (!this.isBuyer) {
      manageForm.controls['selectedPurchasingOffice'].disable({
        emitEvent: false,
      });
    }
    this.disableAssign = false;
    this.disableReassign = true;
    this.disableSubmit = true;
  }

  // Fetch active buyers based on purchasing office
  async fetchActiveBuyers(purchasingOffice: string): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.adminService.getActiveBuyers(purchasingOffice)
      );
      const data = res.body;
      if (data) {
        this.activeBuyers = data.map((b: any) => ({
          userId: b.userId,
          fullName: b.fullName,
        }));
      } else {
        this.activeBuyers = [];
      }
    } catch (error) {
      this.activeBuyers = [];
    }
  }

  private setupFormWatchers(): void {
    this.manageForm
      .get('assignBuyer')
      ?.valueChanges.pipe(skip(1))
      .subscribe(() => {
        this.onAssignBuyerChange();
      });

    this.manageForm
      .get('selectedPurchasingOffice')
      ?.valueChanges.pipe(skip(1))
      .subscribe(() => {
        if (this.isLeadBuyer || this.isBuyer || this.isAdmin) {
          this.onPurchasingOfficeChange();
        }
      });
  }

  private clearLocalStorage(): void {
    localStorage.removeItem('localStorage.groupEmail');
    localStorage.removeItem('localStorage.requestorRatt');
  }

  navigateToView(itemId: number): void {
    const currentItemCount = this.items.length;

    const navigationState = {
      itemCount: currentItemCount,
      itemId: itemId, // Pass itemId through navigation state instead of URL
      pnaNo: this.getPnaNo,
      year: this.getPnaYear,
      returnMode: this.mode,
      pnaIdTemp: this.pnaIdTemp,
      returnUrl: this.getReturnUrl(this.returnUrlRole),
      mode: 'view', // Pass mode through navigation state instead of URL
      role: this.role,
    };

    // Navigate to view page with clean URL - all data passed through state
    this.router.navigate([`/${this.returnUrlRole}/view-item`], {
      state: navigationState,
    });
  }

  navigateToEdit(itemId: string): void {
    const currentItemCount = this.items.length;

    // Prepare navigation state data (hidden from URL)
    const navigationState = {
      itemCount: currentItemCount,
      itemId: itemId, // Pass itemId through navigation state instead of URL
      pnaNo: this.getPnaNo,
      year: this.getPnaYear,
      returnMode: this.mode,
      pnaIdTemp: this.pnaIdTemp,
      returnUrl: this.getReturnUrl(this.returnUrlRole),
      role: this.role,
    };

    // Navigate to edit page with clean URL - itemId passed through state
    this.router.navigate([`/${this.returnUrlRole}/edit-item`], {
      state: navigationState,
    });
  }

  onCompanyCodeChange(event: Event): void {
    if (this.isViewMode) {
      return;
    }

    const selectedCode = event;

    if (selectedCode) {
      let ccdId = this.companyCodes.find(
        (x) => x.ccnCode === selectedCode
      )?.ccnId;

      if (!ccdId) {
        console.warn(
          'Company code ID not found for selected code:',
          selectedCode
        );
        return;
      }

      this.userApi.getCcnBranch(ccdId).subscribe({
        next: (response) => {
          let data = response.body;
          if (data) {
            this.branchPlant = data.branchPlant;
            this.isBranchPlantEnabled = true;
            this.manageForm.get('branchPlant')?.setValue(data.branchPlant);
          }
        },
        error: (err) => {
          console.error('Error fetching branch plant:', err);
          this.branchPlant = '';
          this.isBranchPlantEnabled = false;
        },
      });
    } else {
      this.branchPlant = '';
      this.isBranchPlantEnabled = false;
    }
  }

  toggleDelete(item: any, event: any): void {
    if (event) {
      this.selectedItems.push(item);
    } else {
      this.selectedItems = this.selectedItems.filter(
        (i) =>
          !(
            i.pnaNo === item.pnaNo &&
            i.pnaYear === item.pnaYear &&
            i.itemNo === item.itemNo
          )
      );
    }
  }

  getPriorityColor(priority: string): string {
    const priorityLower = priority?.toLowerCase();

    if (priorityLower === 'fast possible sourcing') return '#ff6b6b';
    if (priorityLower === "don't delay") return '#ffd93d';
    if (priorityLower === 'normal') return '#6bcf7f';

    return '#e9ecef';
  }

  onAddItem(): void {
    this.formState.setFormData(this.manageForm.value);

    // Count current items in the grid
    const currentItemCount = this.items.length;

    // Prepare navigation state data (hidden from URL)
    const navigationState = {
      itemCount: currentItemCount,
      pnaNo: this.getPnaNo,
      year: this.getPnaYear,
      returnMode: this.mode,
      pnaIdTemp: this.pnaIdTemp,
      returnUrl: this.getReturnUrl(this.returnUrlRole),
      role: this.role,
    };

    // Always navigate with clean URL - all data passed through state
    this.router.navigate(['/user/add-item'], {
      state: navigationState,
    });
  }

  getHeading(): string {
    if (this.mode == 'add') {
      return `:: ${this.constants.addNewRequestPnA} ::`;
    }

    if (this.mode == 'edit') {
      if (this.isUser) {
        return `:: ${this.constants.editRequestPnA} ::`;
      }
      if (this.isLeadBuyer) {
        return `:: ${this.constants.assignBuyer} ::`;
      }
      if (this.isBuyer) {
        return `:: ${this.constants.attachInfoPnA} ::`;
      }
      if (this.isAdmin) {
        return `:: ${this.constants.adminAssignBuyer} ::`;
      }
    }

    if (this.mode == 'view') {
      return `:: ${this.constants.viewRequestPnA} ::`;
    }

    if (this.mode == 'otherview') {
      return `:: ${this.constants.viewOtherRequestPnA} ::`;
    }

    return '';
  }

  private checkValid(): boolean {
    const formValues = this.manageForm.value;
    if (!formValues.pnaLocation || formValues.pnaLocation.trim() === '') {
      alert('Please select location.');
      const locationElement = document.querySelector(
        '[formControlName="pnaLocation"]'
      ) as HTMLElement;
      if (locationElement) locationElement.focus();
      return false;
    }
    if (!formValues.requestorName || formValues.requestorName.trim() === '') {
      alert('Please enter Requestor name.');
      const requestorElement = document.querySelector(
        '[formControlName="requestorName"]'
      ) as HTMLElement;
      if (requestorElement) requestorElement.focus();
      return false;
    }
    if (!formValues.pnaTitle || formValues.pnaTitle.trim() === '') {
      alert('Please enter title.');
      const titleElement = document.querySelector(
        '[formControlName="pnaTitle"]'
      ) as HTMLElement;
      if (titleElement) titleElement.focus();
      return false;
    }
    if (formValues.pnaTitle && formValues.pnaTitle.length > 200) {
      alert(
        `Your title is longest, please enter title less than 200 characters. \nYour title characters equals ${formValues.pnaTitle.length}`
      );
      const titleElement = document.querySelector(
        '[formControlName="pnaTitle"]'
      ) as HTMLElement;
      if (titleElement) titleElement.focus();
      return false;
    }
    if (
      !formValues.selectedPurchasingOffice ||
      formValues.selectedPurchasingOffice.trim() === ''
    ) {
      alert('Please select purchasing office.');
      const purchasingElement = document.querySelector(
        '[formControlName="selectedPurchasingOffice"]'
      ) as HTMLElement;
      if (purchasingElement) purchasingElement.focus();
      return false;
    }
    if (!formValues.selectedCompanyCode) {
      alert('Please select company code.');
      const companyCodeElement = document.querySelector(
        '[formControlName="selectedCompanyCode"]'
      ) as HTMLElement;
      if (companyCodeElement) companyCodeElement.focus();
      return false;
    }
    if (this.items.length === 0) {
      alert("You don't have any item in item lists.");
      return false;
    }

    return true;
  }

  saveOrUpdate(
    status: string = 'Draft',
    skipConfirmation: boolean = false
  ): void {
    if (!this.checkValid()) {
      return;
    }

    if (!skipConfirmation && !confirm('Are you sure to save this request?')) {
      return;
    }

    const formValues = this.manageForm.value;
    let pnaNo = this.getPnaNo;

    // For new requests, use pnaIdTemp from URL parameters if available
    if (!pnaNo && this.pnaIdTemp && this.pnaIdTemp !== '0') {
      pnaNo = Number(this.pnaIdTemp);
    }

    const model: PnaDetailsDto = {
      items: this.items?.filter((i) => !i.toDelete) ?? [],
      request: {
        ...this.requestData,
        // Only include pnaNo if we have a valid one, let backend generate for new requests
        ...(pnaNo && pnaNo > 0 ? { pnaNo } : {}),
        pnaYear:
          this.requestData?.pnaYear ?? new Date().getFullYear().toString(),
        pnaLocation: formValues.pnaLocation,
        pnaGroupMail: formValues.groupEmail,
        pnaRequestor: formValues.requestorName,
        pnaRequestorRef: formValues.requestorRatt,
        pnaTitle: formValues.pnaTitle,
        pnaRequestorCai: sessionStorage.getItem('UserEmail') || '',
        pnaCoOwner: formValues.alternateEmail,
        pnaCoOwnerCai: formValues.alternateEmail,
        pnaPurchasing: formValues.selectedPurchasingOffice,
        pnaBranch: formValues.branchPlant,
        pnaCcn: formValues.selectedCompanyCode,
        pnaComment: formValues.comments1,
        pnaStatus: status,
        pnaCreateBy: sessionStorage.getItem('UserEmail') || '',
        pnaUpdateBy: sessionStorage.getItem('UserEmail') || '',
        pnaCreateDate: new Date().toISOString(),
        pnaUpdateDate: new Date().toISOString(),
      },
      comments: formValues.comments1
        ? [
            {
              cmtPnaNo: pnaNo || 0, // Use 0 as placeholder for new requests, backend should update
              cmtPnaYear:
                this.requestData?.pnaYear ??
                new Date().getFullYear().toString(),
              cmtComment: formValues.comments1,
              cmtAttachment: '',
              cmtUpdateDate: new Date().toISOString(),
              cmtUpdateBy: sessionStorage.getItem('UserEmail') || '',
            },
          ]
        : [],
      attachments: this.attachments ?? [],
    };
    const url = this.getPnaNo
      ? `${apiEndpoints.pnaRequestSaveEdit}`
      : `${apiEndpoints.pnaRequestSave}`;

    this.saveOrUpdatePnASubscription = this.userApi
      .saveOrUpdatePnA(url, model)
      .subscribe({
        next: (response) => {
          let data = response.body;
          const isUpdate = this.getPnaNo ? true : false;
          const title =
            status == 'OPEN' ? 'PNA REQUEST SUBMITTED' : 'PNA REQUEST SAVED';
          const message =
            status == 'OPEN' ? 'have been submitted' : 'have been saved';
          const pnaYear =
            this.requestData?.pnaYear || new Date().getFullYear().toString();

          // Store the generated PNA number for new requests if needed
          // Note: This is only necessary if not using pnaIdTemp from URL parameters
          if (!isUpdate && data.pnaNo && !this.pnaIdTemp) {
            localStorage.setItem('currentPnaNo', data.pnaNo.toString());
          }

          this.formState.clearFormData();
          this.showPopup = true;
          this.popupData = {
            title: title,
            pnaNo: data.pnaNo,
            pnaYear: pnaYear,
            message: message,
            returnPath: this.returnPath,
          };
        },
        error: (err) => {
          console.error('Error saving data:', err);
          alert('An error occurred while saving. Please try again.');
        },
      });
  }

  withdraw(): void {
    if (!confirm('Are you sure to withdraw this P&A?')) {
      return;
    }

    if (this.isEditMode) {
      this.formState.clearFormData();

      const pnano = String(this.getPnaNo || '');
      const email = sessionStorage.getItem('UserEmail') || '';

      if (pnano) {
        this.userApi.withdraw(pnano, email).subscribe({
          next: () => {
            this.router.navigate([this.returnPath]);
          },
          error: (err) => {
            alert(JSON.stringify(err.error));
          },
        });
      } else {
        alert('PNA Number not found.');
      }
    }
  }

  back(): void {
    this.formState.clearFormData();
    if ((this.isEditMode && this.isLeadBuyer) || this.isViewMode) {
      this.router.navigate([this.returnPath]);
      return;
    }

    if (this.isAddMode && confirm('Are you sure to cancel this request?')) {
      this.router.navigate([this.returnPath]);
      return;
    }

    if (!this.isAddMode && confirm('Are you sure to back to page list P&A?')) {
      this.router.navigate([this.returnPath]);
      return;
    }
  }

  searchGroupEmail(): void {
    try {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/user-select-email'])
      );

      window.open(url, 'search', 'width=920,height=500,scrollbars=1');
    } catch (error) {
      console.error('Error opening email search window:', error);
      alert('Unable to open email search window. Please try again.');
      return;
    }

    let attempts = 0;
    const maxAttempts = 60;

    const interval = setInterval(() => {
      attempts++;
      const selectedEmail = localStorage.getItem('groupEmail');
      if (selectedEmail) {
        const cleanedEmail = selectedEmail.replace(/[^a-zA-Z0-9@.-]/g, '');

        this.zone.run(() => {
          this.manageForm.get('groupEmail')?.setValue(cleanedEmail);
        });

        localStorage.removeItem('groupEmail');
        clearInterval(interval);
      } else if (attempts >= maxAttempts) {
        console.warn('Search timeout: No email selected within 60 seconds');
        clearInterval(interval);
      }
    }, 1000);
  }

  clearGroupEmail(): void {
    this.manageForm.get('groupEmail')?.setValue('');
    localStorage.removeItem('groupEmail');
  }

  clearEmail(): void {
    this.manageForm.get('alternateEmail')?.setValue('');
    localStorage.removeItem('alternateEmail');
  }

  searchCAI(): void {
    try {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/user-select-cai'])
      );

      window.open(url, 'search', 'width=800,height=600,scrollbars=1');
    } catch (error) {
      console.error('Error opening CAI search window:', error);
      alert('Unable to open CAI search window. Please try again.');
      return;
    }

    let attempts = 0;
    const maxAttempts = 60;

    const interval = setInterval(() => {
      attempts++;
      const getSelectedcai = localStorage.getItem('requestorRatt');
      if (getSelectedcai) {
        const cleanedValue = getSelectedcai.replace(/[^a-zA-Z0-9@.-]/g, '');

        this.zone.run(() => {
          this.alternateEmail = cleanedValue;
          this.manageForm.get('alternateEmail')?.setValue(cleanedValue);
        });

        localStorage.removeItem('requestorRatt');
        clearInterval(interval);
      } else if (attempts >= maxAttempts) {
        console.warn('Search timeout: No CAI selected within 60 seconds');
        clearInterval(interval);
      }
    }, 1000);
  }

  deleteSelectedItems(): void {
    if (confirm('Are you sure to delete checked item?')) {
      const itemIds = this.selectedItems.map(
        (item) => item.itemNoTemp || item.itemNo
      );
      let payload = {};
      if (this.pnaIdTemp && this.pnaYearTemp) {
        payload = {
          PnaNo: this.pnaIdTemp?.toString(),
          PnaYear: this.pnaYearTemp,
          ItemNo: itemIds,
        };
      } else if (this.getPnaNo && this.getPnaYear) {
        payload = {
          PnaNo: this.getPnaNo?.toString(),
          PnaYear: this.getPnaYear,
          ItemNo: itemIds,
        };
      }

      this.userApi.deleteItems(payload).subscribe({
        next: () => {
          alert('Items deleted successfully');
          this.selectedItems = [];
          this.loadPNAData();
        },
        error: (err) => {
          console.error('Delete error:', err);
          alert('Error deleting items');
          this.selectedItems = [];
        },
      });
    }
  }

  async exportExcel() {
    if (!Array.isArray(this.items) || this.items.length === 0) {
      alert('No items to export.');
      return;
    }

    const columns = [
      { header: 'No.', key: 'no', width: 5 },
      { header: 'Manufacturers', key: 'manufacturers', width: 15 },
      { header: 'Item Description', key: 'itemDesc', width: 20 },
      { header: 'Item Qty', key: 'itemQuantity', width: 10 },
      { header: 'Item UOM', key: 'itemUom', width: 10 },
      { header: 'Item Priority', key: 'itemPriority', width: 15 },
      { header: 'JDE Item#', key: 'itemJdeItemNo', width: 15 },
      { header: 'QA Code', key: 'qaCode', width: 10 },
      { header: 'Suggested Supplier', key: 'itemSuggestSupplier', width: 20 },
      { header: 'Comment', key: 'comment', width: 20 },
    ];

    const rows = this.items.map((item, idx) => ({
      no: idx + 1,
      manufacturers: item.itemManufacturer ?? '',
      itemDesc: item.itemDesc ?? '',
      itemQuantity: item.itemQuantity ?? '',
      itemUom: item.itemUom ?? '',
      itemPriority: item.itemPriority ?? '',
      itemJdeItemNo: item.itemJdeItemNo ?? '',
      qaCode: item.itemQa ?? '',
      itemSuggestSupplier: item.itemSuggestSupplier ?? '',
      comment: item.itemComment ?? '',
    }));

    const itemPnaNo = this.items[0]?.itemPnaNo ?? '';
    const itemPnaYear = this.items[0]?.itemPnaYear ?? '';
    const headerRows = [
      [': : Details Items : :'],
      [`P&A No: ${itemPnaNo}-${itemPnaYear}`],
    ];

    await this.excelExportService.exportToExcel(
      rows,
      'PNA_Report',
      columns,
      headerRows,
      'PNA_Report'
    );
  }

  getReturnUrl(module: any): string {
    // Determine the correct return URL based on current route and mode
    const currentUrl = this.router.url;
    if (currentUrl.includes(`${module}/edit-request`)) {
      return `${module}/edit-request`;
    } else if (currentUrl.includes(`${module}/edit-request`)) {
      return `${module}/edit-request`;
    } else if (currentUrl.includes(`${module}/view-other-request`)) {
      return `${module}/view-other-request`;
    } else if (currentUrl.includes(`${module}/view-request`)) {
      return `${module}/view-request`;
    } else {
      return '/user/add-request';
    }
  }

  requestButton(action: string) {
    let actionType = '';
    const manageFormCtrl = this.manageForm.controls;
    const comment = manageFormCtrl['comments1']?.value;

    switch (action) {
      case 'save':
        this.saveOrUpdate('DRAFT');
        break;

      case 'submit':
        if (this.isUser) {
          this.submitForUser();
        }

        if (this.isBuyer) {
          actionType = 'SAVE';

          if (!comment || !comment.trim()) {
            alert(`Please enter the comment.`);
            return;
          }

          if (!confirm('Are you sure to submit this request?')) {
            return;
          }
        }

        break;

      case 'withdraw':
        this.withdraw();
        break;

      case 'assign':
        if (this.isLeadBuyer || this.isAdmin) {
          actionType = 'SAVE';
        } else if (this.isBuyer) {
          actionType = 'REASSIGN';
        }

        const assignBuyer = manageFormCtrl['assignBuyer']?.value;
        if (!assignBuyer) {
          alert('Please select buyer for assign.');
          return;
        }

        // Popup for admin
        if (this.isAdmin) {
          if (!comment || !comment.trim()) {
            alert(
              `Please input reason for re-assign lead buyer on comment box.`
            );
            return;
          }

          if (!confirm('Are you sure to assign buyer?')) {
            return;
          }
        }

        // Popup for lead buyer
        if (this.isLeadBuyer && !confirm('Are you sure to assign buyer?')) {
          return;
        }

        // Popup for buyer
        if (this.isBuyer) {
          if (!comment || !comment.trim()) {
            alert(`Please input reason for re-assign buyer on comment box.`);
            return;
          }

          if (!confirm('Are you sure to submit this request?')) {
            return;
          }
        }

        break;

      case 'reassign':
        if (this.isLeadBuyer || this.isAdmin) {
          actionType = 'REASSIGN';
        } else if (this.isBuyer) {
          actionType = 'REOPEN';
        }

        if (this.isAdmin) {
          if (!comment || !comment.trim()) {
            alert(
              `Please input reason for re-assign lead buyer on comment box.`
            );
            return;
          }
          if (!confirm('Are you sure to re-assign lead buyer?')) {
            return;
          }
        }

        // Popup for lead buyer
        if (this.isLeadBuyer) {
          if (!comment || !comment.trim()) {
            alert(
              `Please input reason for re-assign lead buyer on comment box.`
            );
            return;
          }

          if (!confirm('Are you sure to re-assign lead buyer?')) {
            return;
          }
        }

        // Popup for buyer
        if (this.isBuyer) {
          if (!comment || !comment.trim()) {
            alert(`Please enter the comment.`);
            return;
          }
          if (!confirm('Are you sure to submit this request?')) {
            return;
          }
        }

        break;

      case 'back':
        this.back();
        return;
        break;

      case 'reject':
        if (this.isLeadBuyer || this.isBuyer) {
          actionType = 'REJECT';
        }

        if (!comment || !comment.trim()) {
          alert(`Please input reason for reject P&A on comment box`);
          return;
        }

        if (!confirm('Are you sure to reject P&A?')) {
          return;
        }
        break;

      case 'cancel':
        if (this.isLeadBuyer) {
          actionType = 'CANCEL';
        }

        if (!comment || !comment.trim()) {
          alert(`Please input reason for cancel P&A on comment box.`);
          return;
        }

        if (!confirm('Are you sure to cancel P&A?')) {
          return;
        }
        break;

      case 'reset':
        window.location.reload();
        return;
        break;

      case 'complete':
        if (this.isLeadBuyer || this.isBuyer) {
          actionType = 'CLOSE';
        }

        if (!comment || !comment.trim()) {
          alert(`Please input the comment for complete P&A.`);
          return;
        }

        // Popup for buyer
        if (
          this.isBuyer &&
          !confirm('Are you sure to complete this request?')
        ) {
          return;
        }
        break;

      case 'exportItem':
        this.exportExcel();
        return;
        break;
    }

    if (this.isLeadBuyer) {
      this.leadBuyerAction(actionType, manageFormCtrl);
    } else if (this.isBuyer) {
      this.buyerAction(actionType, manageFormCtrl);
    } else if (this.isAdmin) {
      this.adminAction(actionType, manageFormCtrl);
    }
  }

  leadBuyerAction(actionType: string, manageFormCtrl: any): void {
    const payload = {
      action: actionType,
      pnaNo: this.getPnaNo,
      pnaYear: this.getPnaYear,
      pnaTitle: manageFormCtrl['pnaTitle']?.value,
      requestorName: manageFormCtrl['requestorName']?.value,
      requestoremail: manageFormCtrl['requestorEmail']?.value,
      buyerId: manageFormCtrl['assignBuyer']?.value || 0,
      buyerName:
        this.activeBuyers.find(
          (b) => b.userId === manageFormCtrl['assignBuyer']?.value
        )?.fullName || '',
      purchasing: manageFormCtrl['selectedPurchasingOffice']?.value,
      comment: manageFormCtrl['comments1']?.value,
      updatedBy: sessionStorage.getItem('UserEmail') || '',
    };

    this.pnaRequestLeadBuyerActionSubscription = this.leadBuyerServiceService
      .pnaRequestLeadBuyerAction(payload)
      .subscribe({
        next: () => {
          const { title, message } = this.getActionTitleAndMessage(
            actionType,
            'lead-buyer'
          );

          this.showPopup = true;
          this.popupData = {
            title: title,
            pnaNo: payload.pnaNo || 0,
            pnaYear: payload.pnaYear || '',
            message: message,
            returnPath: this.returnPath,
          };
        },
        error: (err) => {
          alert('Failed to assign buyer. Please try again.');
          console.error('API error:', err);
        },
      });
  }

  buyerAction(actionType: string, manageFormCtrl: any): void {
    const formData = new FormData();

    formData.append('PnaYear', this.getPnaYear || '0');
    formData.append('PnaNo', (this.getPnaNo ?? '0').toString());
    formData.append(
      'RequestorEmail',
      manageFormCtrl['requestorEmail']?.value || ''
    );
    formData.append('BuyerId', manageFormCtrl['assignBuyer']?.value || 0);
    formData.append('UpdatedBy', sessionStorage.getItem('UserEmail') || '');
    formData.append('PNATitle', manageFormCtrl['pnaTitle']?.value || '');
    formData.append(
      'RequestorName',
      manageFormCtrl['requestorName']?.value || ''
    );
    formData.append(
      'Purchasing',
      manageFormCtrl['selectedPurchasingOffice']?.value || ''
    );
    formData.append(
      'BuyerName',
      this.activeBuyers.find(
        (b) => b.userId === manageFormCtrl['assignBuyer']?.value
      )?.fullName || ''
    );
    formData.append('Comment', manageFormCtrl['comments1']?.value || '');

    // If you have a file to attach
    formData.append('Attachment', this.commentAttachmentfile); // file should be a File object

    this.pnaRequestBuyerActionSubscription = this.buyerServiceService
      .pnaRequestBuyerAction(formData, actionType)
      .subscribe({
        next: () => {
          const { title, message } = this.getActionTitleAndMessage(
            actionType,
            'buyer'
          );

          this.showPopup = true;
          this.popupData = {
            title: title,
            pnaNo: this.getPnaNo || 0,
            pnaYear: this.getPnaYear || '',
            message: message,
            returnPath: this.returnPath,
          };
        },
        error: (err) => {
          alert('Failed to process buyer action. Please try again.');
          console.error('API error:', err);
        },
      });
  }

  adminAction(actionType: string, manageFormCtrl: any): void {
    const payload = {
      action: actionType,
      pnaNo: this.getPnaNo,
      pnaYear: this.getPnaYear,
      pnaTitle: manageFormCtrl['pnaTitle']?.value,
      requestorName: manageFormCtrl['requestorName']?.value,
      requestoremail: manageFormCtrl['requestorEmail']?.value,
      buyerId: manageFormCtrl['assignBuyer']?.value || 0,
      buyerName:
        this.activeBuyers.find(
          (b) => b.userId === manageFormCtrl['assignBuyer']?.value
        )?.fullName || '',
      purchasing: manageFormCtrl['selectedPurchasingOffice']?.value,
      comment: manageFormCtrl['comments1']?.value,
      updatedBy: sessionStorage.getItem('UserEmail') || '',
    };

    this.updatePnaRequestSubscription = this.adminManageService
      .updatePnaRequest(payload)
      .subscribe({
        next: () => {
          const { title, message } = this.getActionTitleAndMessage(
            actionType,
            'admin'
          );

          this.showPopup = true;
          this.popupData = {
            title: title,
            pnaNo: payload.pnaNo || 0,
            pnaYear: payload.pnaYear || '',
            message: message,
            returnPath: this.returnPath,
          };
        },
        error: (err) => {
          alert('Failed to assign buyer. Please try again.');
          console.error('API error:', err);
        },
      });
  }

  getActionTitleAndMessage(
    actionType: string,
    userType: string
  ): {
    title: string;
    message: string;
  } {
    switch (actionType) {
      case 'SAVE':
        if (userType == 'buyer') {
          return {
            title: 'SUBMIT',
            message: 'have been sent Quotation to requestor.',
          };
        }
        return {
          title: 'ASSIGN BUYER',
          message: 'have been assigned buyer.',
        };
      case 'REASSIGN':
        if (userType == 'buyer') {
          return {
            title: 'BUYER RE-ASSIGNED',
            message: 'have been re-assigned buyer.',
          };
        }
        return {
          title: 'BUYER RE-ASSIGNED',
          message: 'have been re-assigned lead buyer.',
        };
      case 'REJECT':
        return {
          title: 'PNA REJECTED',
          message: 'have been rejected.',
        };
      case 'CANCEL':
        return {
          title: 'PNA CANCELED',
          message: 'have been canceled.',
        };
      case 'CLOSE':
        return {
          title: 'PNA CLOSED',
          message: 'have been closed..',
        };
      case 'REOPEN':
        return {
          title: 'RE-ASSIGN Purchasing Office',
          message: 'have been sent Quotation to requestor.',
        };
      default:
        return {
          title: 'ACTION COMPLETED',
          message: 'have been processed.',
        };
    }
  }

  submitForUser(): void {
    if (!this.checkValid()) {
      return;
    }

    if (!confirm('Are you sure to submit this request?')) {
      return;
    }

    this.saveOrUpdate('OPEN', true);
    this.formState.clearFormData();

    return;
  }

  onFileChange(event: any): void {
    this.commentAttachmentfile = event.target.files[0];
  }

  downloadManual(path: string): void {
    this.downloadFileSubscription = this.fileService
      .downloadFile(path)
      .subscribe({
        next: (response: any) => {
          if (response.body) {
            const contentDisposition = response.headers.get(
              'Content-Disposition'
            );
            const filename =
              this.getFilenameFromContentDisposition(contentDisposition);
            const blob = new Blob([response.body]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
          } else {
            console.error('Response body is empty or invalid.');
          }
        },
        error: (error) => {
          console.error('Error downloading file:', error);
        },
      });
  }

  private getFilenameFromContentDisposition(
    contentDisposition: string | null
  ): string {
    if (!contentDisposition) return 'downloaded-file';

    const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
      contentDisposition
    );
    return matches != null && matches[1]
      ? matches[1].replace(/['"]/g, '')
      : 'downloaded-file';
  }

  goDelete(commentDetails: any) {
    if (!commentDetails) {
      return;
    }

    const payload = {
      cmtId: commentDetails.cmtId,
      cmtPnaNo: commentDetails.cmtPnaNo,
      cmtPnaYear: commentDetails.cmtPnaYear,
      cmtComment: commentDetails.cmtComment,
      cmtAttachment: commentDetails.cmtAttachment,
      cmtUpdateDate: commentDetails.cmtUpdateDate,
      cmtUpdateBy: commentDetails.cmtUpdateBy,
    };

    this.removeBuyersAttachmentSubscription = this.fileService
      .removeBuyersAttachment(payload)
      .subscribe({
        next: () => {
          alert('Attachment deleted successfully!');
          this.loadPNAData();
        },
        error: (error: any) => {
          alert(
            'Failed to delete attachment. Error: ' +
              (error?.message || 'Unknown error')
          );
        },
      });
  }

  get isGridEmpty(): boolean {
    return this.items.length === 0;
  }

  get isDraft(): boolean {
    if (!this.status) return true;
    return this.status.toLowerCase() === 'draft';
  }

  get isOpen(): boolean {
    if (!this.status) return false;
    return this.status.toLowerCase() === 'open';
  }

  get isInProgress(): boolean {
    if (!this.status) return false;
    return this.status.toLowerCase() === 'in progress';
  }

  get isPartialQuoted(): boolean {
    if (!this.status) return false;
    return this.status.toLowerCase() === 'partial quoted';
  }

  get isDeleted(): boolean {
    if (!this.status) return false;
    return this.status.toLowerCase() === 'deleted';
  }

  get isClosed(): boolean {
    if (!this.status) return false;
    return this.status.toLowerCase() === 'closed';
  }

  get isCanceled(): boolean {
    if (!this.status) return false;
    return this.status.toLowerCase() === 'canceled';
  }

  get isEditDisabled(): boolean {
    return !this.isDraft;
  }

  get showSaveButton(): boolean {
    return (this.isEditMode || this.isAddMode) && this.isUser;
  }

  get showSubmitButton(): boolean {
    return (
      (this.isEditMode && (this.isUser || this.isBuyer)) ||
      (this.isAddMode && this.isUser)
    );
  }

  get isSaveSubmitDisabled(): boolean {
    return this.isOpen && this.mode === 'edit';
  }

  get showWithdrawButton(): boolean {
    return this.isUser && this.isEditMode;
  }

  get showResetButton(): boolean {
    return this.isEditMode && (this.isLeadBuyer || this.isAdmin);
  }

  get showCancelButton(): boolean {
    return this.isEditMode && this.isLeadBuyer;
  }

  get showAssignButton(): boolean {
    return (
      this.isEditMode && (this.isAdmin || this.isBuyer || this.isLeadBuyer)
    );
  }

  get showReAssignButton(): boolean {
    return (
      this.isEditMode && (this.isAdmin || this.isBuyer || this.isLeadBuyer)
    );
  }

  get showRejectButton(): boolean {
    return this.isEditMode && (this.isBuyer || this.isLeadBuyer);
  }

  get showExportItemButton(): boolean {
    return this.isEditMode && this.isBuyer;
  }

  get showCompletePnAButton(): boolean {
    return this.isEditMode && this.isBuyer;
  }

  get isWithdrawDisabled(): boolean {
    return this.isUser && this.isEditMode && !this.isOpen;
  }

  get isSubmitDisabled(): boolean {
    return (
      this.disableSubmit || (this.isUser && this.isEditMode && this.isOpen)
    );
  }

  get showCheckboxes(): boolean {
    if (this.isOpen && this.mode === 'edit') {
      return false;
    }
    return (
      (this.isDraft || this.mode === 'add') &&
      !this.isViewMode &&
      this.mode !== 'otherview' &&
      this.isUser
    );
  }

  get showEditAction(): boolean {
    if (this.isDraft && this.mode === 'edit') {
      return false;
    }

    return (
      (this.isDraft || this.mode === 'add') &&
      !this.isViewMode &&
      this.mode !== 'otherview' &&
      !this.isAdmin
    );
  }

  get showViewAction(): boolean {
    if (this.isViewMode) {
      return true;
    }

    if (!this.status) return false;
    const statusLower = this.status.toLowerCase();
    const showForStatuses = [
      'open',
      'deleted',
      'in progress',
      'inprogress',
      'partial quoted',
    ];
    return showForStatuses.includes(statusLower) && !this.isAdmin;
  }

  get showAddDeleteButtons(): boolean {
    return (
      (this.isDraft || this.mode === 'add') &&
      !this.isAdmin &&
      !this.isViewMode &&
      this.mode !== 'otherview'
    );
  }

  get showNewCommentBox(): boolean {
    if (this.isOpen && this.mode === 'edit') {
      return true;
    }
    return !this.isViewMode && this.mode !== 'otherview';
  }

  get showItemViewAction(): boolean {
    return this.isEditMode || this.isViewMode;
  }

  get showItemEditAction(): boolean {
    if ((this.isOpen && this.isEditMode) || (this.isDraft && this.isEditMode)) {
      return false;
    }
    return this.showEditAction && !this.isViewMode;
  }

  get showAssignBuyers(): boolean {
    return this.isAdmin || this.isLeadBuyer || this.isBuyer;
  }

  get showSearchEmailandReset(): boolean {
    return !this.isViewMode && this.isUser;
  }

  ngOnDestroy(): void {
    this.getLocationsSubscription?.unsubscribe();
    this.companiesSubscription?.unsubscribe();
    this.officeSubscription?.unsubscribe();
    this.saveOrUpdatePnASubscription?.unsubscribe();
    this.pnaRequestLeadBuyerActionSubscription?.unsubscribe();
    this.pnaRequestBuyerActionSubscription?.unsubscribe();
    this.updatePnaRequestSubscription?.unsubscribe();
    this.downloadFileSubscription?.unsubscribe();
    this.removeBuyersAttachmentSubscription?.unsubscribe();
  }
}
